import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { FeatherIcon } from '../../FeatherIcon';

interface Props {
  accountId: string;
  url: string | null;
  type: string | null;
  caption?: string | null;
  onClose: () => void;
  // Per-item Pinned price (ADR 0042). When `contentId` + `onSavePins` are provided, the
  // viewer shows an inline pin editor (price + optional firm floor). Omitted ⇒ no editor.
  contentId?: string | null;
  pinnedPrice?: number | null;
  pinnedFloor?: number | null;
  onSavePins?: (price: number | null, floor: number | null) => void;
  /** Step through a set of items (arrow buttons + ←/→ keys). Omitted ⇒ single item. */
  onPrev?: () => void;
  onNext?: () => void;
}

const pinInputCls =
  'w-24 px-2 py-1 rounded-md border border-white/15 bg-white/5 text-sm text-white placeholder-white/40 focus:border-[var(--color-accent-primary)] focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed';

/** Parse a price field to a positive number, or null when blank/invalid. */
function parsePrice(raw: string): number | null {
  const v = raw.trim();
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function SalesMediaViewer({
  accountId,
  url,
  type,
  caption,
  onClose,
  contentId,
  pinnedPrice,
  pinnedFloor,
  onSavePins,
  onPrev,
  onNext,
}: Props) {
  const { t } = useTranslation('sales');
  const { t: tc } = useTranslation('content');

  const [price, setPrice] = useState<string>(pinnedPrice != null ? String(pinnedPrice) : '');
  const [floor, setFloor] = useState<string>(pinnedFloor != null ? String(pinnedFloor) : '');
  const [pinError, setPinError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  if (!url) return null;
  const src = api.proxyMediaUrl(accountId, url);
  const isVideo = (type || '').toLowerCase() === 'video';
  const isAudio = (type || '').toLowerCase() === 'audio';
  const showPinEditor = !!(contentId && onSavePins);

  // Persist the current pin (called on blur). Clearing the price clears the floor — a floor
  // with no price is invalid. A floor above the price is rejected inline, not saved.
  const savePins = () => {
    const p = parsePrice(price);
    const f = parsePrice(floor);
    if (p === null) {
      if (floor !== '') setFloor('');
      setPinError(null);
      onSavePins?.(null, null);
    } else if (f !== null && f > p) {
      setPinError(tc('contentCard.pinnedFloorError'));
      return;
    } else {
      setPinError(null);
      onSavePins?.(p, f);
    }
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1500);
  };

  const navBtn = 'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white';

  // Portalled: a transformed/filtered ancestor would otherwise trap the fixed overlay.
  return createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t('salesMediaViewer.close')}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
      >
        <FeatherIcon name="x" size={18} />
      </button>
      {onPrev && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label={t('salesMediaViewer.prev')} className={`${navBtn} left-4`}>
          <FeatherIcon name="chevron-left" size={22} />
        </button>
      )}
      {onNext && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label={t('salesMediaViewer.next')} className={`${navBtn} right-4`}>
          <FeatherIcon name="chevron-right" size={22} />
        </button>
      )}
      <div
        className="max-w-[95vw] max-h-[calc(95*var(--om-vh))] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideo ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="max-w-[95vw] max-h-[calc(78*var(--om-vh))] rounded-lg shadow-2xl"
          />
        ) : isAudio ? (
          <div className="flex flex-col items-center gap-5 px-10 py-12 rounded-2xl bg-zinc-900 shadow-2xl">
            <span
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'color-mix(in srgb, var(--color-accent-primary) 18%, transparent)', color: 'var(--accent-ico, var(--om-legacy-pale))' }}
            >
              <FeatherIcon name="music" size={34} />
            </span>
            <audio src={src} controls autoPlay className="w-[min(80vw,380px)]" />
          </div>
        ) : (
          <img
            src={src}
            alt={caption ?? ''}
            className="max-w-[95vw] max-h-[calc(78*var(--om-vh))] object-contain rounded-lg shadow-2xl"
          />
        )}
        {caption && (
          <div className="mt-3 px-4 py-2 max-w-[60ch] text-sm text-white/90 bg-black/40 rounded">
            {caption}
          </div>
        )}

        {/* Per-item Pinned price editor (ADR 0042): an exact on-demand price for THIS item that
            overrides the pricing grid wherever it is sold (bundle sends keep their own set
            price). Optional floor is the haggle limit; disabled until a price is set. */}
        {showPinEditor && (
          <div
            className="mt-3 w-full max-w-[60ch] px-4 py-3 rounded-lg bg-black/55 border border-white/10 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/90">
                <FeatherIcon name="tag" size={13} />
                {tc('contentCard.pinnedPrice')}
              </span>
              {saved && (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                  <FeatherIcon name="check" size={11} />
                  {tc('contentCard.pinnedPriceSaved')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                aria-label={tc('contentCard.pinnedPrice')}
                placeholder={tc('contentCard.pinnedPricePlaceholder')}
                value={price}
                onChange={(e) => {
                  setPinError(null);
                  setPrice(e.target.value);
                  if (parsePrice(e.target.value) === null) setFloor('');
                }}
                onBlur={savePins}
                className={pinInputCls}
              />
              <span className="text-white/40 text-sm">/</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                aria-label={tc('contentCard.pinnedFloorPlaceholder')}
                placeholder={tc('contentCard.pinnedFloorPlaceholder')}
                value={floor}
                disabled={parsePrice(price) === null}
                onChange={(e) => {
                  setPinError(null);
                  setFloor(e.target.value);
                }}
                onBlur={savePins}
                className={pinInputCls}
              />
            </div>
            {pinError ? (
              <p className="text-[11px] text-red-300">{pinError}</p>
            ) : (
              <p className="text-[11px] text-white/50">{tc('contentCard.pinnedPriceTitle')}</p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
